/**
 * LIVE / DELAYED / CLOSED — the dot that says an intraday chart is live
 * (owner-approved mockup, 2026-09-29). Drawn in the chart toolbar left of ⛶;
 * on the phone, which has no toolbar, over the chart's top-right corner.
 */

import { useSyncExternalStore } from "react";
import type { LiveState } from "../lib/intraday";
import { liveBadgeOf, subscribeLive } from "../lib/live-badge";

const TITLE: Record<LiveState, string> = {
  live: "Live — today's bars stream from the market feed; the last bar is still forming.",
  delayed: "Live feed unavailable — updating every 5 s from the backup source.",
  closed: "Market closed — these bars are final.",
};

export function LiveBadge({
  state,
  tick,
  height,
  dotOnly = false,
}: {
  state: LiveState;
  tick: number;
  height?: number;
  /** The folded toolbar's form: the dot, the word in the tooltip. */
  dotOnly?: boolean;
}) {
  return (
    <div
      title={TITLE[state]}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        height,
        boxSizing: "border-box",
        padding: "0.1rem 0.4rem",
        borderRadius: 4,
        border: "1px solid var(--qf-border)",
        background: "var(--qf-surface)",
        color: "var(--qf-text-secondary)",
        fontSize: "0.7rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          flex: "0 0 auto",
          borderRadius: "50%",
          background:
            state === "live" ? "var(--qf-positive)" : state === "delayed" ? "var(--qf-warn)" : "var(--qf-text-muted)",
          // A gentle pulse, once a second, only while live — the tick that
          // already moves the countdown, so no animation runs on its own.
          opacity: state === "live" && tick % 2 === 1 ? 0.45 : 1,
          transition: "opacity 0.4s ease",
        }}
      />
      {!dotOnly && (state === "live" ? "LIVE" : state === "delayed" ? "DELAYED" : "CLOSED")}
    </div>
  );
}

/** The badge a chart published under its id, or nothing (daily charts, no frame yet). */
export function ChartLiveBadge({
  chartGroupId,
  height,
  dotOnly,
}: {
  chartGroupId: string;
  height?: number;
  dotOnly?: boolean;
}) {
  const badge = useSyncExternalStore(subscribeLive, () => liveBadgeOf(chartGroupId));
  return badge ? (
    <LiveBadge state={badge.state} tick={badge.tick} height={height} dotOnly={dotOnly} />
  ) : null;
}
